/* =========================================================
   Innovation pillars — relevo de pilares con la sección fijada

   ESCRITORIO (>=750px)
   Una sola timeline con scrub gobierna todos los pilares. El tiempo de la
   timeline se mide en "tramos", uno por block: con 3 pilares la timeline dura
   3 unidades, con 5 dura 5. Nada está hardcodeado al número de pilares.

     tramo i = [i, i+1]
       t = i        -> el pilar i entra (opacity 0 -> 1 mientras sube de +shift
                       a 0) y, a la vez, el pilar i-1 sale (opacity 1 -> 0
                       mientras sigue subiendo, de 0 a -shift). Ese solape ES
                       el relevo: nunca hay dos pilares visibles salvo durante
                       el cruce.
       resto        -> el pilar i se queda quieto y legible.

   El último pilar no tiene salida: si se fuera, la sección se despinearía con
   el escenario vacío. Se queda hasta el final del pin y entonces el scroll
   sigue hacia la siguiente sección.

   La timeline dura exactamente `count` unidades gracias al tween vacío del
   final: sin él duraría hasta la última entrada (count - 1 + fade) y el último
   pilar no llegaría a tener su tramo completo de lectura.

   Distancia total del pin = count * altura de pantalla * "scroll por pilar".
   Se calcula dentro de la función `end` y con invalidateOnRefresh, así que se
   recalcula sola al cambiar el viewport o al añadir pilares en el editor.

   MÓVIL (<=749px)
   Sin animación: los pilares son un listado en flujo normal, uno debajo de
   otro, siempre visibles. Lo resuelve el CSS; aquí no se monta nada. Las
   entradas/salidas por scroll de antes daban saltos con el scroll táctil.

   La rama de escritorio vive en gsap.matchMedia(), así que al pasar a móvil
   se revierte entera (tweens, sets, pin y ScrollTriggers). No quedan estilos
   inline residuales.

   Quien oculta los pilares es autoAlpha (opacity + visibility), no el CSS: un
   pilar invisible queda además fuera del árbol de accesibilidad y no captura
   clics. Ver la cabecera de assets/innovation-pillars.css.
   ========================================================= */

(function () {
  'use strict';

  /* En el Theme Editor la sección re-inyecta su <script> y este archivo se
     vuelve a ejecutar. Si ya hay un módulo vivo, reutilizamos el suyo. */
  if (window.DermxInnovationPillars) {
    window.DermxInnovationPillars.initAll();
    return;
  }

  var GSAP_VERSION = '3.13.0';
  var GSAP_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/' + GSAP_VERSION + '/gsap.min.js';
  var SCROLLTRIGGER_URL =
    'https://cdnjs.cloudflare.com/ajax/libs/gsap/' + GSAP_VERSION + '/ScrollTrigger.min.js';

  var DESKTOP = '(min-width: 750px)';
  var MOTION_OK = '(prefers-reduced-motion: no-preference)';
  var MOTION_REDUCED = '(prefers-reduced-motion: reduce)';

  /* Una instancia por section.id: { mm, cleanup } */
  var instances = new Map();

  /* ---------------------------------------------------------
     Carga de GSAP + ScrollTrigger (una sola vez por página)
     --------------------------------------------------------- */

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-gsap-src="' + src + '"]');

      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
        } else {
          existing.addEventListener('load', function () {
            resolve();
          });
          existing.addEventListener('error', reject);
        }
        return;
      }

      var script = document.createElement('script');
      script.src = src;
      script.async = false; // preserva el orden gsap -> ScrollTrigger
      script.dataset.gsapSrc = src;
      script.addEventListener('load', function () {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  function isPluginRegistered(name) {
    return !!(window.gsap && window.gsap.core && window.gsap.core.globals()[name]);
  }

  /* Promesa compartida con smooth-scroll.js y el resto de secciones: la primera
     que llegue monta GSAP y las demás se enganchan a la misma promesa. */
  function ensureGsap() {
    if (window.dermxGsapReady) return window.dermxGsapReady;

    window.dermxGsapReady = Promise.resolve()
      .then(function () {
        if (!window.gsap) return loadScript(GSAP_URL);
        return null;
      })
      .then(function () {
        if (!window.gsap) throw new Error('[innovation-pillars] GSAP no disponible');
        if (!window.ScrollTrigger && !isPluginRegistered('ScrollTrigger')) {
          return loadScript(SCROLLTRIGGER_URL);
        }
        return null;
      })
      .then(function () {
        var gsap = window.gsap;
        var ScrollTrigger = window.ScrollTrigger || gsap.core.globals().ScrollTrigger;

        if (!ScrollTrigger) throw new Error('[innovation-pillars] ScrollTrigger no disponible');
        if (!isPluginRegistered('ScrollTrigger')) gsap.registerPlugin(ScrollTrigger);

        ScrollTrigger.config({ ignoreMobileResize: true });

        return { gsap: gsap, ScrollTrigger: ScrollTrigger };
      });

    return window.dermxGsapReady;
  }

  /* ---------------------------------------------------------
     Utilidades
     --------------------------------------------------------- */

  var refreshTimer = null;

  function refreshSoon(ScrollTrigger) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      ScrollTrigger.refresh();
    }, 60);
  }

  function refreshIfReady() {
    if (window.gsap && isPluginRegistered('ScrollTrigger')) {
      refreshSoon(window.gsap.core.globals().ScrollTrigger);
    }
  }

  /* Un icono que aún no ha cargado cambia el alto del pilar más alto y, con él,
     el alto de la celda apilada: hay que volver a medir. */
  function refreshOnImages(root, ScrollTrigger) {
    root.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener(
        'load',
        function () {
          refreshSoon(ScrollTrigger);
        },
        { once: true }
      );
    });
  }

  /* La webfont cambia el alto del texto y por tanto el de la sección fijada. */
  function refreshOnFonts(ScrollTrigger) {
    if (!document.fonts || !document.fonts.ready) return;
    document.fonts.ready.then(function () {
      refreshSoon(ScrollTrigger);
    });
  }

  function killTween(tween) {
    if (tween.scrollTrigger) tween.scrollTrigger.kill(true);
    tween.kill();
  }

  /* ---------------------------------------------------------
     Ciclo de vida por sección
     --------------------------------------------------------- */

  function destroy(sectionId) {
    var instance = instances.get(sectionId);
    if (!instance) return;
    if (instance.cleanup) instance.cleanup();
    instance.mm.revert(); // mata tweens, sets, pins y ScrollTriggers de esta sección
    instances.delete(sectionId);
  }

  function init(root) {
    if (!root) return;

    var sectionId = root.dataset.sectionId;
    if (!sectionId) return;

    destroy(sectionId); // evita duplicados al re-renderizar en el editor

    ensureGsap()
      .then(function (lib) {
        // El editor puede haber quitado la sección mientras cargaba el CDN.
        if (!root.isConnected) return;
        build(root, sectionId, lib.gsap, lib.ScrollTrigger);
      })
      .catch(function (error) {
        // Sin GSAP: pilares apilados en flujo y visibles, también en escritorio.
        root.classList.add('innovation-pillars--static');
        console.warn(error);
      });
  }

  function build(root, sectionId, gsap, ScrollTrigger) {
    var select = gsap.utils.selector(root); // todo scopeado a esta sección
    var pillars = select('[data-pillar]');
    var count = pillars.length;

    if (!count) return;

    var shift = parseFloat(root.dataset.shift) || 40;

    /* Fracción del viewport que hay que recorrer para pasar de un pilar al
       siguiente. Multiplicada por el número de pilares da el largo del pin. */
    var pillarScroll = (parseFloat(root.dataset.pillarScroll) || 90) / 100;

    /* Parte del tramo que ocupa el cruce. El resto es tiempo de lectura, así
       que no puede comerse el tramo entero. */
    var fade = gsap.utils.clamp(0.1, 0.7, (parseFloat(root.dataset.fadeRatio) || 35) / 100);

    var mm = gsap.matchMedia();
    var instance = { mm: mm, cleanup: null };
    instances.set(sectionId, instance);

    /* --- Escritorio: pin + una sola timeline dividida en `count` tramos --- */
    function pinnedSequence() {
      root.classList.add('innovation-pillars--pinned');

      /* ease: 'none' en toda la timeline. Con scrub, el usuario ya controla el
         tiempo con el scroll; una curva encima haría que el cruce de opacidad
         entre dos pilares no sumara 1 y se vería un bajón de luz a mitad. */
      var timeline = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          id: 'innovation-pillars-' + sectionId,
          trigger: root,
          start: 'top top',
          end: function () {
            return '+=' + Math.round(count * window.innerHeight * pillarScroll);
          },
          scrub: true,
          pin: root,
          pinSpacing: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      pillars.forEach(function (pillar, index) {
        // Entrada: aparece ascendiendo, no aparece y luego sube.
        timeline.fromTo(
          pillar,
          { autoAlpha: 0, y: shift },
          { autoAlpha: 1, y: 0, duration: fade },
          index
        );

        // Salida: se va por arriba mientras se apaga. El último no sale.
        if (index < count - 1) {
          timeline.to(pillar, { autoAlpha: 0, y: -shift, duration: fade }, index + 1);
        }
      });

      /* Tween vacío para que la timeline dure `count` exactos: le da al último
         pilar su tramo completo de lectura antes de que la sección se despinee. */
      timeline.to({}, { duration: 0 }, count);

      return function () {
        killTween(timeline);
        root.classList.remove('innovation-pillars--pinned');
      };
    }

    /* --- Movimiento reducido: todo en flujo, visible y quieto --- */
    mm.add(MOTION_REDUCED, function () {
      root.classList.add('innovation-pillars--static');
      return function () {
        root.classList.remove('innovation-pillars--static');
      };
    });

    /* Móvil no monta nada: listado estático resuelto en CSS. */
    mm.add(DESKTOP + ' and ' + MOTION_OK, pinnedSequence);

    /* Resize con debounce: el largo del pin depende de window.innerHeight y el
       alto de la celda apilada depende del reflujo del texto. */
    var resizeTimer = null;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        ScrollTrigger.refresh();
      }, 150);
    }

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    instance.cleanup = function () {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };

    refreshOnImages(root, ScrollTrigger);
    refreshOnFonts(ScrollTrigger);
    refreshSoon(ScrollTrigger);
  }

  /* ---------------------------------------------------------
     Arranque + eventos del Theme Editor
     --------------------------------------------------------- */

  function initAll() {
    document.querySelectorAll('.innovation-pillars[data-section-id]').forEach(init);
  }

  window.DermxInnovationPillars = { init: init, initAll: initAll, destroy: destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('load', refreshIfReady);

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('.innovation-pillars[data-section-id]');
    if (!root) return;
    init(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('.innovation-pillars[data-section-id]');
    if (!root) return;
    destroy(root.dataset.sectionId);
  });

  /* Añadir, quitar o reordenar pilares cambia el número de tramos y el largo
     del pin. El editor re-renderiza la sección entera (shopify:section:load) en
     esos casos; esto cubre además el select/deselect, que solo mueve offsets. */
  [
    'shopify:section:select',
    'shopify:section:deselect',
    'shopify:block:select',
    'shopify:block:deselect',
  ].forEach(function (eventName) {
    document.addEventListener(eventName, refreshIfReady);
  });
})();
